/**
 * Share widget — "Copy link" behaviour for `template-parts/part-share-widget.php`.
 *
 * Shared by the single post and single Job Position sidebars; the network
 * links are real URLs and need no JS.
 */

/**
 * Initializes the Share widget: "Copy link" via the Clipboard
 * API (falling back to `execCommand` for older browsers), with a visible
 * status message for all users including screen reader users.
 *
 * @param {HTMLElement} root Element containing the `[data-share-widget]` markup.
 * @return {void}
 */
export const initShareWidget = ( root ) => {

	const widget = root.querySelector( '[data-share-widget]' );

	if ( !widget ) {

		return;

	}

	const copyButton = widget.querySelector( '[data-share-copy]' );

	if ( !copyButton ) {

		return;

	}

	const statusEl = widget.querySelector( '[data-share-status]' );
	const shareUrl = widget.dataset.shareUrl || window.location.href;

	const STATUS_TIMEOUT_MS = 1000;
	let statusTimer = null;

	/**
	 * Shows a status message, auto-clearing it after a short delay so it
	 * doesn't linger indefinitely once read (or announced by a screen reader).
	 *
	 * @param {string} message Status message to display.
	 * @return {void}
	 */
	const setStatus = ( message ) => {

		if ( !statusEl ) {

			return;

		}

		clearTimeout( statusTimer );
		statusEl.textContent = message;

		statusTimer = setTimeout( () => {

			statusEl.textContent = '';

		}, STATUS_TIMEOUT_MS );

	};

	/**
	 * Copies the shared URL to the clipboard, showing a status message.
	 *
	 * @return {Promise<void>}
	 */
	const copyLink = async () => {

		try {

			if ( navigator.clipboard?.writeText ) {

				await navigator.clipboard.writeText( shareUrl );

			} else {

				const input = document.createElement( 'input' );
				input.value = shareUrl;
				input.style.position = 'fixed';
				input.style.opacity = '0';
				document.body.appendChild( input );
				input.select();
				document.execCommand( 'copy' ); // Legacy clipboard fallback for browsers without navigator.clipboard.
				input.remove();

			}

			setStatus( copyButton.dataset.copiedText || 'Link copied' );

		} catch ( error ) {

			setStatus( copyButton.dataset.errorText || '' );

		}

	};

	copyButton.addEventListener( 'click', copyLink );

};
